/**
 * collection-sort-availability.js
 *
 * Ordena os produtos da coleção colocando disponíveis antes dos esgotados,
 * buscando todas as páginas da coleção e construindo uma paginação virtual
 * no cliente (sem recarregar a página).
 *
 * ── Como incluir ────────────────────────────────────────────────────────────
 * Adicione a tag abaixo no template de coleção (ex: sections/main-collection-product-grid.liquid):
 *
 *   <script src="{{ 'collection-sort-availability.js' | asset_url }}" defer></script>
 *
 * ── Pré-requisitos no HTML ───────────────────────────────────────────────────
 * O script depende de três elementos no DOM:
 *
 *   1. Grid de produtos com id="product-grid" e os data attributes:
 *        data-id="{{ section.id }}"
 *        data-per-page="{{ section.settings.products_per_page }}"
 *        data-total-pages="{{ paginate.pages }}"
 *
 *   2. Container externo com id="ProductGridContainer" — observado via
 *      MutationObserver para detectar re-renders do Dawn após filtros/sort AJAX.
 *
 *   3. Wrapper de paginação com classe "pagination-wrapper" — onde a paginação
 *      virtual será injetada.
 *
 * ── Marcação de disponibilidade nos cards ────────────────────────────────────
 * Cada <li> do grid deve ter o data attribute abaixo para que o script saiba
 * quais produtos estão esgotados:
 *
 *   data-product-available="{{ product.available }}"
 *   (Shopify renderiza como "true" ou "false")
 *
 * ── Contador de produtos (opcional) ─────────────────────────────────────────
 * Para exibir "Mostrando X–Y de Z produtos", adicione o atributo em qualquer
 * elemento da página:
 *
 *   <span data-collection-count></span>
 *
 * ── Constantes ajustáveis ────────────────────────────────────────────────────
 * Se o tema mudar os IDs/seletores padrão do Dawn, edite as constantes no topo
 * do script (GRID_ID, GRID_CONTAINER_ID, PAGINATION_SEL, MAX_PAGES).
 *
 * ── Comportamento ────────────────────────────────────────────────────────────
 * - Coleção com 1 página: reordena os cards diretamente no DOM (sem fetch).
 * - Coleção com múltiplas páginas: faz fetch paralelo de todas as páginas,
 *   une os cards, ordena e constrói paginação virtual client-side.
 * - Compatível com filtros e ordenação AJAX do Dawn (detecta re-renders via
 *   MutationObserver e reinicia o sort automaticamente).
 * - Compatível com navegação via browser back/forward (popstate).
 * - Em caso de erro no fetch, cai no reorder de DOM como fallback.
 */

(function () {
  'use strict';

  // ─── Selectors (adapt these if the theme changes) ──────────────────────────
  var GRID_ID           = 'product-grid';
  var GRID_CONTAINER_ID = 'ProductGridContainer';
  var PAGINATION_SEL    = '.pagination-wrapper';
  var MAX_PAGES         = 20;

  // ─── State ─────────────────────────────────────────────────────────────────
  var state = {
    cards        : [],   // { html, soldOut }[]
    perPage      : 8,
    sectionId    : '',
    totalPages   : 1,
    initialized  : false,
    fetching     : false,
    generation   : 0,
    arrowPrevHtml: '',
    arrowNextHtml: '',
  };

  // ─── Read metadata from #product-grid data attributes ─────────────────────
  function readMeta() {
    var el = document.getElementById(GRID_ID);
    if (!el) return null;
    return {
      sectionId : el.dataset.id,
      perPage   : parseInt(el.dataset.perPage, 10)   || 24,
      totalPages: parseInt(el.dataset.totalPages, 10) || 1,
    };
  }

  // ─── URL builder — preserves existing filter/sort params ──────────────────
  function buildUrl(page, sectionId) {
    var params = new URLSearchParams(window.location.search);
    params.set('page', page);
    params.set('section_id', sectionId);
    return window.location.pathname + '?' + params.toString();
  }

  // ─── Fetch all server pages in parallel ───────────────────────────────────
  function fetchAllPages(sectionId, totalPages) {
    var count = Math.min(totalPages, MAX_PAGES);
    var fetches = [];
    for (var i = 1; i <= count; i++) {
      fetches.push(
        fetch(buildUrl(i, sectionId)).then(function (r) { return r.text(); })
      );
    }
    return Promise.all(fetches);
  }

  // ─── Extract <li> cards from fetched HTML ─────────────────────────────────
  function extractCards(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(doc.querySelectorAll('#' + GRID_ID + ' > li')).map(function (li) {
      return {
        html   : li.outerHTML,
        soldOut: li.dataset.productAvailable === 'false',
        element: li, // Guardar elemento para busca
      };
    });
  }

  // ─── Filter cards by search term ──────────────────────────────────────────
  function filterCardsBySearchTerm(cards, searchTerm) {
    if (!searchTerm || searchTerm.trim() === '') return cards;
    
    var term = searchTerm.toLowerCase().trim();
    
    return cards.filter(function(card) {
      // Busca no título do produto
      var titleEl = card.element.querySelector('.card__heading a, .card-information__text a, h3 a');
      var title = titleEl ? titleEl.textContent.toLowerCase() : '';
      
      // Busca no vendor/marca
      var vendorEl = card.element.querySelector('.card__vendor, .caption-with-letter-spacing');
      var vendor = vendorEl ? vendorEl.textContent.toLowerCase() : '';
      
      // Busca no SKU/ID do produto (se disponível)
      var sku = card.element.dataset.productId || '';
      
      // Retorna true se encontrar o termo em qualquer campo
      return title.includes(term) || vendor.includes(term) || sku.includes(term);
    });
  }

  // ─── Sort: available first, sold-out last ─────────────────────────────────
  function sortCards(cards) {
    var available = cards.filter(function (c) { return !c.soldOut; });
    var soldOut   = cards.filter(function (c) { return c.soldOut; });
    return available.concat(soldOut);
  }

  // ─── Capture arrow SVGs from the server-rendered pagination ───────────────
  function captureArrows() {
    var wrapper = document.querySelector(PAGINATION_SEL);
    if (!wrapper) return;
    // Dawn's pagination uses .pagination__item--next for "prev" arrow and vice-versa
    var prevEl = wrapper.querySelector('.pagination__item--next .svg-wrapper');
    var nextEl = wrapper.querySelector('.pagination__item--prev .svg-wrapper');
    if (prevEl) state.arrowPrevHtml = prevEl.outerHTML;
    if (nextEl) state.arrowNextHtml = nextEl.outerHTML;
  }

  // ─── Update count text elements ───────────────────────────────────────────
  function updateCount(pageNum) {
    var total = state.cards.length;
    var start = (pageNum - 1) * state.perPage + 1;
    var end   = Math.min(pageNum * state.perPage, total);
    var text  = 'Mostrando ' + start + '\u2013' + end + ' de ' + total + ' produtos';
    document.querySelectorAll('[data-collection-count]').forEach(function (el) {
      el.textContent = text;
    });
  }

  // ─── Render a page slice into the grid ────────────────────────────────────
  function renderPage(pageNum) {
    var grid = document.getElementById(GRID_ID);
    if (!grid || state.cards.length === 0) return;

    var start = (pageNum - 1) * state.perPage;
    var slice = state.cards.slice(start, start + state.perPage);
    if (slice.length === 0) return;

    grid.innerHTML = slice.map(function (c) { return c.html; }).join('');

    // Reset animation order on inserted items
    Array.from(grid.children).forEach(function (li, i) {
      li.style.setProperty('--animation-order', i + 1);
      li.style.opacity = '1';
      li.removeAttribute('reveal');
    });
  }

  // ─── Build virtual pagination (no page reload) ────────────────────────────
  function buildPagination(currentPage) {
    var wrapper = document.querySelector(PAGINATION_SEL);
    if (!wrapper) return;

    var totalPages = Math.ceil(state.cards.length / state.perPage);

    if (totalPages <= 1) {
      wrapper.innerHTML = '';
      return;
    }

    // Build dots
    var dots = '';
    for (var i = 1; i <= totalPages; i++) {
      var activeClass = i === currentPage ? ' pagination__dot--active' : '';
      var ariaCurrent = i === currentPage ? ' aria-current="page"' : '';
      dots += '<li>'
        + '<a href="#" class="pagination__dot' + activeClass + '"'
        + ' data-vs-page="' + i + '" aria-label="Página ' + i + '"' + ariaCurrent + '>'
        + '&nbsp;'
        + '</a></li>';
    }

    // Build arrows
    var prevArrow = '';
    var nextArrow = '';

    if (currentPage > 1) {
      prevArrow = '<a href="#" class="pagination__arrow pagination__arrow--prev"'
        + ' data-vs-page="' + (currentPage - 1) + '" aria-label="Página anterior">'
        + (state.arrowPrevHtml || '<span class="svg-wrapper">&#8249;</span>')
        + '</a>';
    } else {
      prevArrow = '<button class="pagination__arrow pagination__arrow--prev" disabled aria-label="Página anterior">'
        + (state.arrowPrevHtml || '<span class="svg-wrapper">&#8249;</span>')
        + '</button>';
    }

    if (currentPage < totalPages) {
      nextArrow = '<a href="#" class="pagination__arrow pagination__arrow--next"'
        + ' data-vs-page="' + (currentPage + 1) + '" aria-label="Próxima página">'
        + (state.arrowNextHtml || '<span class="svg-wrapper">&#8250;</span>')
        + '</a>';
    } else {
      nextArrow = '<button class="pagination__arrow pagination__arrow--next" disabled aria-label="Próxima página">'
        + (state.arrowNextHtml || '<span class="svg-wrapper">&#8250;</span>')
        + '</button>';
    }

    wrapper.innerHTML = '<ul class="pagination__dots" role="list">' + dots + '</ul>'
      + '<div class="pagination__arrows">' + prevArrow + nextArrow + '</div>';

    wrapper.dataset.page = currentPage;
  }

  // ─── Navigate to a virtual page ───────────────────────────────────────────
  function navigateTo(pageNum) {
    renderPage(pageNum);
    buildPagination(pageNum);
    updateCount(pageNum);

    var url = new URL(window.location.href);
    if (pageNum === 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', pageNum);
    }
    history.pushState({ vsPage: pageNum }, '', url.toString());

    // Scroll to top of grid container
    var container = document.getElementById(GRID_CONTAINER_ID);
    if (container) {
      var top = container.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }

  // ─── Attach click handler to the virtual pagination ───────────────────────
  function attachPaginationListeners() {
    var wrapper = document.querySelector(PAGINATION_SEL);
    if (!wrapper) return;
    wrapper.removeEventListener('click', onPaginationClick);
    wrapper.addEventListener('click', onPaginationClick);
  }

  function onPaginationClick(e) {
    var link = e.target.closest('a[data-vs-page]');
    if (!link) return;
    e.preventDefault();
    var page = parseInt(link.dataset.vsPage, 10);
    if (!isNaN(page)) navigateTo(page);
  }

  // ─── In-page DOM reorder (fallback for single-page collections) ───────────
  function reorderDOM() {
    var grid = document.getElementById(GRID_ID);
    if (!grid) return;
    
    var items = Array.from(grid.children);
    
    // Filtrar por termo de busca se existir parâmetro q na URL
    var urlParams = new URLSearchParams(window.location.search);
    var searchTerm = urlParams.get('q');
    
    if (searchTerm && searchTerm.trim() !== '') {
      var term = searchTerm.toLowerCase().trim();
      items = items.filter(function(li) {
        // Busca no título do produto
        var titleEl = li.querySelector('.card__heading a, .card-information__text a, h3 a');
        var title = titleEl ? titleEl.textContent.toLowerCase() : '';
        
        // Busca no vendor/marca
        var vendorEl = li.querySelector('.card__vendor, .caption-with-letter-spacing');
        var vendor = vendorEl ? vendorEl.textContent.toLowerCase() : '';
        
        // Busca no SKU/ID do produto (se disponível)
        var sku = li.dataset.productId || '';
        
        // Retorna true se encontrar o termo em qualquer campo
        return title.includes(term) || vendor.includes(term) || sku.includes(term);
      });
    }
    
    // Ordenar: disponíveis primeiro, esgotados depois
    var available = items.filter(function (li) { return li.dataset.productAvailable !== 'false'; });
    var soldOut   = items.filter(function (li) { return li.dataset.productAvailable === 'false'; });
    
    // Limpar grid e adicionar apenas os itens filtrados e ordenados
    grid.innerHTML = '';
    available.concat(soldOut).forEach(function (li) { grid.appendChild(li); });
    
    // Atualizar contador de produtos
    var countEls = document.querySelectorAll('[data-collection-count]');
    if (countEls.length > 0) {
      var total = items.length;
      var text = 'Mostrando 1\u2013' + total + ' de ' + total + ' produtos';
      countEls.forEach(function (el) { el.textContent = text; });
    }
  }

  // ─── Virtual sort orchestrator ────────────────────────────────────────────
  function initVirtualSort() {
    var meta = readMeta();
    if (!meta) return;

    state.sectionId = meta.sectionId;
    state.perPage   = meta.perPage;
    state.totalPages = meta.totalPages;

    // Single-page collection: just reorder in DOM, no fetch needed
    if (meta.totalPages <= 1) {
      reorderDOM();
      return;
    }

    var gen = ++state.generation;
    state.fetching    = false;
    state.initialized = false;
    state.fetching    = true;

    // Capture arrows before we replace server-rendered pagination
    captureArrows();

    // Hide grid to prevent flash of unsorted content
    var grid = document.getElementById(GRID_ID);
    if (grid) grid.style.visibility = 'hidden';

    fetchAllPages(state.sectionId, state.totalPages)
      .then(function (pages) {
        if (gen !== state.generation) return; // Stale — a newer sort was triggered

        var all = [];
        pages.forEach(function (html) {
          all = all.concat(extractCards(html));
        });

        // Filtrar por termo de busca se existir parâmetro q na URL
        var urlParams = new URLSearchParams(window.location.search);
        var searchTerm = urlParams.get('q');
        if (searchTerm) {
          all = filterCardsBySearchTerm(all, searchTerm);
        }

        state.cards       = sortCards(all);
        state.fetching    = false;
        state.initialized = true;

        renderPage(1);
        buildPagination(1);
        updateCount(1);
        attachPaginationListeners();

        if (grid) grid.style.visibility = '';
      })
      .catch(function (err) {
        console.warn('[sort-availability] Fetch failed, falling back to in-page sort.', err);
        if (gen !== state.generation) return;
        state.fetching = false;
        if (grid) grid.style.visibility = '';
        reorderDOM();
      });
  }

  // ─── MutationObserver — detect Dawn's facets/sort AJAX updates ────────────
  var _debounceTimer = null;

  function watchForUpdates() {
    var container = document.getElementById(GRID_CONTAINER_ID);
    if (!container) return;

    var observer = new MutationObserver(function (mutations) {
      // Only react when #product-grid itself is (re)inserted into the DOM
      var gridInserted = mutations.some(function (m) {
        return Array.from(m.addedNodes).some(function (n) {
          if (n.nodeType !== 1) return false;
          return n.id === GRID_ID
            || (typeof n.querySelector === 'function' && !!n.querySelector('#' + GRID_ID));
        });
      });

      if (!gridInserted || state.fetching) return;

      // Debounce: wait for URL (history.pushState) to update after DOM change
      clearTimeout(_debounceTimer);
      _debounceTimer = setTimeout(function () {
        state.cards       = [];
        state.initialized = false;
        initVirtualSort();
      }, 150);
    });

    observer.observe(container, { childList: true, subtree: true });
  }

  // ─── Handle browser back/forward navigation ───────────────────────────────
  window.addEventListener('popstate', function (e) {
    if (state.initialized && e.state && e.state.vsPage) {
      renderPage(e.state.vsPage);
      buildPagination(e.state.vsPage);
    }
  });

  // ─── Boot ─────────────────────────────────────────────────────────────────
  function boot() {
    if (!document.getElementById(GRID_ID)) return;
    initVirtualSort();
    watchForUpdates();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
